import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const customization = useSelector((state) => state.customization);
  const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

  const isSelected = customization.isOpen.findIndex((id) => id === item.id) > -1;

  // Every item takes the same dot, whatever `icon` the menu definition carries.
  //
  // The icons were not carrying information: most of the rail repeated the SAME clipboard
  // glyph, so a reader scanned a column of identical shapes that distinguished nothing
  // while adding weight. Items with no icon already fell back to this dot, which is why
  // Request Form and its neighbours looked calmer than everything above them.
  //
  // Now that sections collapse, the group header does the orienting and the item only has
  // to read as a line of text. The dot marks position without competing with the label,
  // and it grows slightly on the current page, so selection stays visible.
  //
  // The `icon` fields are left in menu-items on purpose: they cost nothing, and reverting
  // to per-item icons stays a one-line change here.
  const itemIcon = (
    <FiberManualRecordIcon
      sx={{
        width: isSelected ? 8 : 6,
        height: isSelected ? 8 : 6
      }}
      fontSize="inherit"
      color="inherit"
    />
  );

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = {
    component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch({ type: MENU_OPEN, id });
    if (matchesSM) dispatch({ type: SET_MENU, opened: false });
  };

  // active menu item on page load
  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split('/')
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch({ type: MENU_OPEN, id: item.id });
    }
    // eslint-disable-next-line
  }, [pathname]);

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      sx={{
        borderRadius: '7px',
        mb: 0,
        // Centred, not top-aligned: a wrapped label used to push its dot to the first line
        // and leave the row looking broken.
        alignItems: 'center',
        backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
        // Tighter rows. At py 1.25 the long labels ("Training Requests", "In-App
        // Notifications") wrapped to two lines in a 260px rail, which is most of why the
        // expanded list felt endless.
        py: 0.6,
        // Stepped in from the section header, which sits flush left. The indent is what
        // says "these belong to that" — size alone would not.
        pl: level > 1 ? '30px' : '22px',
        pr: 1
      }}
      selected={isSelected}
      onClick={() => itemHandler(item.id)}
    >
      {/* One width for every item now that every item has the same dot. This was 18 or 36
          depending on whether the item had an icon, which is why labels in the same list
          started at two different left edges. */}
      <ListItemIcon sx={{ my: 'auto', minWidth: 16, color: 'inherit' }}>{itemIcon}</ListItemIcon>
      <ListItemText
        sx={{ my: 0 }}
        primary={
          <Typography
            variant="body2"
            sx={{ color: 'inherit', fontWeight: isSelected ? 600 : 400, lineHeight: 1.35 }}
          >
            {item.title}
          </Typography>
        }
        secondary={
          item.caption && (
            <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
              {item.caption}
            </Typography>
          )
        }
      />
      {item.chip && (
        // `color="#ffffff"` isn't a valid MUI Chip colour — MUI expects a palette key, so
        // this was emitting a prop warning and falling back to the default anyway.
        <Chip
          color="secondary"
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
